<template>
  <div class="hedge-tracker grid" :class="{ 'compact-mode': !calcMode }">

        <div class="grid grid-2 box-rounded">

            <div class="metric-card">
                <div class="metric-card-label">Delta PNL (Net)</div>
                <div class="metric-card-value" :class="hedgeMetrics.netPnl >= 0 ? 'profit-positive' : 'profit-negative'">{{ hedgeMetrics.netPnl.toFixed(2) }}</div>
                <div class="metric-card-secondary">CoveredPct: {{ hedgeMetrics.netCoveredPct.toFixed(2) }}% | Uncovered Qty: {{ hedgeMetrics.uncoveredQty.toFixed(6) }}</div>
            </div>

            <div class="metric-card">
                <div class="metric-card-label">Portfolio Geral PNL</div>
                <div class="metric-card-value" :class="portfolioMetrics.portfolioPnl >= 0 ? 'profit-positive' : 'profit-negative'">{{ portfolioMetrics.portfolioPnl.toFixed(2) }}</div>
                <div class="metric-card-secondary">Qty: {{ portfolioMetrics.portfolioQty.toFixed(6) }} | Price: {{ portfolioMetrics.portfolioAvgPrice.toFixed(2) }}</div>
            </div>

            <div class="metric-card">
                <div class="metric-card-label">Hedge PNL</div>
                <div class="metric-card-value" :class="hedgeMetrics.pnl >= 0 ? 'profit-positive' : 'profit-negative'">{{ hedgeMetrics.pnl.toFixed(2) }}</div>
                <div class="metric-card-secondary">Qty: {{ (hedgeInfos.hedgeSize || 0).toFixed(6) }} | Price: {{ (hedgeInfos.hedgeEntry || 0).toFixed(2) }}</div>
            </div>

            <div class="metric-card">
                <div class="metric-card-label">Total Qty Below Hedge</div>
                <div class="metric-card-value">{{ hedgeMetrics.totalUnitsBelowHedge.toFixed(6) }}</div>
            </div>

        </div>

      <div class="grid box-rounded units-panel" v-if="calcMode">

        <div class="grid grid-2">

          <div class="metric-card">
            <div class="metric-card-label">Open P/L Units</div>
            <div v-if="selectedUnitIds.length">
              <div class="metric-card-value" :class="selectedUnitsMetrics.deltaPnl >= 0 ? 'profit-positive' : 'profit-negative'">{{ selectedUnitsMetrics.deltaPnl.toFixed(2) }} USD</div>
              <div class="metric-card-secondary">Qty: {{ selectedUnitsMetrics.selectedQty.toFixed(6) }} | Avg: {{ selectedUnitsMetrics.avgPrice.toFixed(2) }}</div>
              <div class="metric-card-secondary"> Δ Price: {{ selectedUnitsMetrics.deltaPrice.toFixed(2) }}</div>
            </div>
            <div v-else>Selecione units para comparar.</div>
          </div>

          <div class="metric-card">
            <div class="metric-card-label">Locked P/L Units vs Hedge</div>
            <div v-if="selectedUnitIds.length">
              <div class="metric-card-value" :class="selectedUnitsMetrics.hedgeVsUnitsPnl >= 0 ? 'profit-positive' : 'profit-negative'">{{ selectedUnitsMetrics.hedgeVsUnitsPnl.toFixed(2) }} USD</div>
              <div class="metric-card-secondary">Units Qty: {{ selectedUnitsMetrics.selectedQty.toFixed(6) }} | Avg: {{ selectedUnitsMetrics.avgPrice.toFixed(2) }}</div>
              <div class="metric-card-secondary">Hedge Qty: {{ hedgeInfos.hedgeSize.toFixed(6) }} | Price: {{ hedgeInfos.hedgeEntry.toFixed(2) }}</div>
            </div>
            <div v-else>Selecione units para comparar.</div>
          </div>

        </div>

          <div class="units-selector">
            <div class="units-label">Units (clique para selecionar)</div>
            <div class="grid grid-3">
              <button v-for="unit in manualUnits" :key="unit.unitID" class="unit-chip" :class="{ 'selected': selectedUnitIds.includes(unit.unitID), 'positive': (Number(unit.current?.avgPrice) < hedgeInfos.hedgeEntry) }" @click="toggleUnit(unit.unitID)">
                <div class="unit-id">{{ unit.unitID }}</div>
                <div class="unit-sub"> Avg: {{ Number(unit.current?.avgPrice || 0).toFixed(2) }} | Qty: {{ Number(unit.current?.qty || 0).toFixed(6) }}</div>
                <div class="unit-sub">Source: {{ unit.metadata?.excgAccount   || 'N/A' }}</div>
                <div class="unit-sub">P/L: {{ calcSpreadPercent(unit.current.avgPrice, marketData.btcUsd) }}%</div>
              </button>
            </div>
          </div>

      </div>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import { calcSpreadPercent } from '@/utils/funcs';

export default {
  name: 'hedgeTracker',
  props: {
    calcMode: { type: Boolean, default: false }
  },
  data() {
    return {
      selectedUnitIds: []
    };
  },
  computed: {
    ...mapState({
      marketData: state => state.marketData, kpis: state => state.kpis, hedgeInfos: state => state.hedgeInfos || { hedgeSize: 0, hedgeEntry: 0 },
      manualUnits: state => Object.values(state.ledger || {}).filter(item => item.bucket === 'manual').sort((a, b) => (a.current?.avgPrice || 0) - (b.current?.avgPrice || 0))
    }),

    portfolioMetrics() {

      const kpis = this.kpis
      const portfolioQty = kpis.portfolioVolumeRegulars + kpis.portfolioVolumeManuals;
      const portfolioAvgPrice = ((kpis.portfolioPMPRegulars * kpis.portfolioVolumeRegulars) + (kpis.portfolioPMPManuals * kpis.portfolioVolumeManuals)) / portfolioQty;
      const portfolioPnl = (this.marketData.btcUsd - portfolioAvgPrice) * portfolioQty;

      return { portfolioQty, portfolioPnl, portfolioAvgPrice }
    },

    hedgeMetrics() {

      const portfolioMetrics = this.portfolioMetrics;
      const hedgeInfos = this.hedgeInfos;

      const pnl = (hedgeInfos.hedgeEntry - this.marketData.btcUsd) * hedgeInfos.hedgeSize;
      const netPnl = portfolioMetrics.portfolioPnl + pnl;
      const uncoveredQty = portfolioMetrics.portfolioQty - hedgeInfos.hedgeSize;
      const netAvgPrice = (portfolioMetrics.portfolioAvgPrice * portfolioMetrics.portfolioQty) - (hedgeInfos.hedgeEntry * hedgeInfos.hedgeSize);

      const totalUnitsBelowHedge = this.manualUnits.filter(unit => Number(unit.current?.avgPrice || 0) < hedgeInfos.hedgeEntry).reduce((sum, unit) => sum + Number(unit.current?.qty || 0), 0);
      const netCoveredPct = (this.hedgeInfos.hedgeSize / this.portfolioMetrics.portfolioQty)*100; //totalUnitsBelowHedge / portfolioMetrics.portfolioQty * 100;

      return { pnl, uncoveredQty, netPnl, netAvgPrice, totalUnitsBelowHedge, netCoveredPct }
    },

    selectedUnitsMetrics() {
      const set = new Set(this.selectedUnitIds);
      const selectedUnits = this.manualUnits.filter(unit => set.has(unit.unitID));
      const selectedQty = selectedUnits.reduce((sum, unit) => sum + (Number(unit.current?.qty) || 0), 0);

      if (selectedQty <= 0) return { selectedQty: 0, avgPrice: 0, deltaPrice: 0, deltaPnl: 0, hedgeVsUnitsPnl: 0 };

      const totalValue = selectedUnits.reduce((sum, u) => sum + (Number(u.current?.avgPrice || 0) * Number(u.current?.qty || 0)), 0);
      const avgPrice = totalValue / selectedQty;

      const deltaPrice = this.marketData.btcUsd - avgPrice;
      const deltaPnl = deltaPrice * selectedQty;
      const hedgeVsUnitsPnl = deltaPnl + this.hedgeMetrics.pnl;

      return { selectedQty, avgPrice, deltaPrice, deltaPnl, hedgeVsUnitsPnl };
    },

  },
  methods: {
    calcSpreadPercent,
    toggleUnit(unitId) {
      if (this.selectedUnitIds.includes(unitId)) {
        this.selectedUnitIds = this.selectedUnitIds.filter(id => id !== unitId);
      } else {
        this.selectedUnitIds = [...this.selectedUnitIds, unitId];
      }
    }
  }
};
</script>

<style scoped>
.compact-mode {
  grid-template-columns: 1fr !important;  /* empilha os dois painéis */
}

.hedge-tracker {
  grid-template-columns: 1fr 1fr;
  background: var(--table-header-bgcolor);
  color: var(--text-color);
  border-radius: 10px;
  padding: 12px;
}

.hedge-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.positive {
  background-color: #4caf4f9d !important;
}

.profit-positive {
  color: #4caf50;
}

.profit-negative {
  color: #f44336;
}

.units-selector {
  height: 400px !important;
}

.units-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.units-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.units-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.unit-tag {
  background: #1f1f1f;
  border: 1px solid #2b2b2b;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
}
</style>
